import { useState, useCallback, useRef, useEffect } from "react";

export interface MediaStreamState {
  stream: MediaStream | null;
  videoEnabled: boolean;
  audioEnabled: boolean;
  isLoading: boolean;
  error: string | null;
  permissionStatus: "prompt" | "granted" | "denied" | "unknown";
}

export const useMediaStream = () => {
  const [state, setState] = useState<MediaStreamState>({
    stream: null,
    videoEnabled: true,
    audioEnabled: true,
    isLoading: false,
    error: null,
    permissionStatus: "unknown",
  });

  const streamRef = useRef<MediaStream | null>(null);

  // Check permission status
  const checkPermissions = useCallback(async () => {
    try {
      const camera = await navigator.permissions.query({ name: "camera" as PermissionName });
      const mic = await navigator.permissions.query({ name: "microphone" as PermissionName });
      
      const status = camera.state === "granted" && mic.state === "granted"
        ? "granted"
        : camera.state === "denied" || mic.state === "denied"
        ? "denied"
        : "prompt";
      
      setState((prev) => ({ ...prev, permissionStatus: status }));
      return status;
    } catch {
      // Some browsers don't support permission query
      return "unknown";
    }
  }, []);

  // Request media stream (camera + mic)
  const startStream = useCallback(async (video = true, audio = true): Promise<MediaStream | null> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: video ? { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } } : false,
        audio: audio ? { echoCancellation: true, noiseSuppression: true } : false,
      });

      streamRef.current = stream;
      setState({
        stream,
        videoEnabled: video,
        audioEnabled: audio,
        isLoading: false,
        error: null,
        permissionStatus: "granted",
      });

      return stream;
    } catch (err: any) {
      let errorMsg = "Failed to access camera/microphone";
      
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        errorMsg = "Camera/microphone permission denied. Please allow access in your browser settings.";
        setState((prev) => ({ ...prev, permissionStatus: "denied" }));
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        errorMsg = "No camera or microphone found on this device.";
      } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
        errorMsg = "Camera/microphone is already in use by another application.";
      }

      setState((prev) => ({
        ...prev,
        stream: null,
        isLoading: false,
        error: errorMsg,
      }));

      return null;
    }
  }, []);

  // Stop all tracks and release stream
  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      streamRef.current = null;
    }
    setState((prev) => ({
      ...prev,
      stream: null,
      videoEnabled: true,
      audioEnabled: true,
      error: null,
    }));
  }, []);

  // Toggle video track
  const toggleVideo = useCallback(() => {
    if (streamRef.current) {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setState((prev) => ({ ...prev, videoEnabled: videoTrack.enabled }));
      }
    }
  }, []);

  // Toggle audio track
  const toggleAudio = useCallback(() => {
    if (streamRef.current) {
      const audioTrack = streamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setState((prev) => ({ ...prev, audioEnabled: audioTrack.enabled }));
      }
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  return {
    ...state,
    startStream,
    stopStream,
    toggleVideo,
    toggleAudio,
    checkPermissions,
  };
};
